import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import Icon1 from "@assets/icons/Icon";
import { useTagStore } from "@store/tagStore";
import { useDataStore } from "@store/user_org";
import { getContrastTextColor } from "@utils/tagColor";
import TagCreateInline from "./TagCreateInline";
import TagCategoryGroups from "./TagCategoryGroups";

interface InlineTagEditorProps {
  scope: string;
  tagIds: string[];
  onSave: (tagIds: string[]) => void;
  /** Bouton « + Ajouter un tag » toujours affiché, même quand des tags sont
   *  posés. Pour les fiches (modale, détail) ; les tableaux gardent l'apparition
   *  au survol pour ne pas charger chaque ligne. */
  alwaysShowAdd?: boolean;
}

/**
 * Inline tag editor for table cells and detail views.
 * Click to open a popover, toggle tags, changes save immediately.
 */
export default function InlineTagEditor({ scope, tagIds, onSave, alwaysShowAdd = false }: InlineTagEditorProps) {
  // La liste des tags n'est chargée que par certains écrans (Ordres, Dossiers…) : ouverte
  // depuis le planning réception ou l'atelier, la fiche n'en avait aucun.
  const orgId = useDataStore((s) => s.orgData?.id);
  useEffect(() => { if (orgId) useTagStore.getState().fetchTags(orgId); }, [orgId]);
  const tags = useTagStore((s) => s.tags)
    .filter((t) => t.scope === scope && t.active)
    .sort((a, b) => a.name.localeCompare(b.name));
  const [isOpen, setIsOpen] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const triggerRef = useRef<HTMLDivElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);

  const resolvedTags = tags.filter((t) => tagIds.includes(t.id));

  const handleToggle = (tagId: string) => {
    const next = tagIds.includes(tagId)
      ? tagIds.filter((id) => id !== tagId)
      : [...tagIds, tagId];
    onSave(next);
  };

  const handleOpen = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    if (triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const top = spaceBelow > 270 ? rect.bottom + 4 : rect.top - 270;
      setPosition({ top, left: rect.left });
    }
    setIsOpen(true);
  };

  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (triggerRef.current?.contains(target)) return;
      if (popoverRef.current?.contains(target)) return;
      setIsOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  const popover = isOpen ? createPortal(
    <div
      ref={popoverRef}
      className="bg-white shadow-lg rounded-xl border flex flex-col"
      style={{
        position: "fixed",
        top: position.top,
        left: position.left,
        zIndex: 10001, // above BaseModal (z-[10000])
        width: 260,
        maxHeight: 270,
      }}
      onClick={(e) => e.stopPropagation()}
    >
      <div className="px-3 py-2 border-b">
        <span className="text-h4 text-neutral-400">Tags</span>
      </div>
      <div className="overflow-y-auto p-2 flex flex-row flex-wrap gap-1.5">
        <TagCategoryGroups tags={tags} renderTag={(tag) => {
          const isSelected = tagIds.includes(tag.id);
          return (
            <span
              key={tag.id}
              className="text-h4 whitespace-nowrap cursor-pointer select-none"
              onClick={(e) => { e.stopPropagation(); handleToggle(tag.id); }}
              style={{
                background: isSelected ? tag.color : "transparent",
                color: isSelected ? getContrastTextColor(tag.color) : tag.color,
                border: `1.5px solid ${tag.color}`,
                borderRadius: 30,
                padding: "2px 10px",
                lineHeight: "16px",
                opacity: isSelected ? 1 : 0.7,
              }}
            >
              {tag.name}
            </span>
          );
        }} />
        {tags.length === 0 && (
          <div className="px-2 py-3 text-h4 text-neutral-400 text-center w-full">
            Aucun tag encore
          </div>
        )}
      </div>
      {/* Un tag qui manque se crée ici même, et se pose sur l'ordre dans la foulée. */}
      <div className="px-2 py-2 border-t">
        <TagCreateInline
          scope={scope}
          onCreated={(id) => { if (!tagIds.includes(id)) onSave([...tagIds, id]); }}
        />
      </div>
    </div>,
    document.body,
  ) : null;

  return (
    <>
      <div
        ref={triggerRef}
        className="flex flex-row items-center gap-1 flex-wrap cursor-pointer min-h-[28px] group"
        onClick={handleOpen}
      >
        {resolvedTags.length > 0 && (
          resolvedTags.map((tag) => (
            <span
              key={tag.id}
              className="text-h4 whitespace-nowrap"
              style={{
                background: tag.color,
                color: getContrastTextColor(tag.color),
                borderRadius: 30,
                padding: "2px 10px",
                lineHeight: "16px",
              }}
            >
              {tag.name}
            </span>
          ))
        )}
        {alwaysShowAdd ? (
          <span
            className="text-h4 font-medium whitespace-nowrap flex items-center gap-1 transition-colors text-neutral-500 hover:text-neutral-800 hover:border-neutral-400"
            style={{ border: "1.5px dashed #D4D4D4", borderRadius: 30, padding: "1px 10px", lineHeight: "16px" }}
          >
            <Icon1 name="plus" size={12} color="currentColor" />
            {resolvedTags.length > 0 ? "Tag" : "Ajouter un tag"}
          </span>
        ) : resolvedTags.length === 0 && (
          <span className="text-h4 text-neutral-300 opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-1">
            <Icon1 name="plus" size={12} color="#D4D4D4" />
            Tag
          </span>
        )}
      </div>
      {popover}
    </>
  );
}
